import { createTheme, getActionPalette, type ThemeMode } from 'src/lib/style/theme';
import { baseTheme } from 'src/theme';
import { DASHBOARD_BACKGROUND_COLORS, RESQ_BRAND_COLORS } from 'src/constants/palette';

/**
 * Build vendor-specific theme with light or dark mode support.
 * @param mode - 'light' or 'dark'
 * @returns Theme object with vendor customizations
 */
export const buildVendorTheme = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const actionPalette = getActionPalette(mode);

    const vendorTheme = createTheme({
        ...baseTheme,
        palette: {
            ...baseTheme.palette,
            mode, // Set MUI palette mode
            primary: actionPalette.primary.mui,
            secondary: actionPalette.secondary.mui,
            text: {
                primary: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                secondary: isDark ? '#9AA0A6' : RESQ_BRAND_COLORS.slate,
            },
            background: {
                default: isDark
                    ? DASHBOARD_BACKGROUND_COLORS.dark
                    : DASHBOARD_BACKGROUND_COLORS.light,
                paper: isDark ? '#1A1F26' : '#FFFFFF',
            },
        },
        typography: {
            ...baseTheme.typography,
            body3: {
                ...baseTheme.typography.body3,
                color: isDark ? '#9AA0A6' : RESQ_BRAND_COLORS.slate,
            },
        },
        resqPalette: {
            background: {
                default: isDark
                    ? DASHBOARD_BACKGROUND_COLORS.dark
                    : DASHBOARD_BACKGROUND_COLORS.light,
            },
            surface: {
                // Core surfaces
                default: isDark ? '#1A1F26' : '#FFFFFF',
                subdued: isDark ? '#242A33' : '#F0F1F1',
                hovered: isDark ? '#2D3540' : RESQ_BRAND_COLORS.cream,
                pressed: isDark ? '#1E2A3D' : '#EAF1FB',
                selected: isDark ? '#1A3334' : '#DBF6F6',
                // Primary surfaces
                primary: actionPalette.primary.surface,
                primaryHovered: actionPalette.primary.hovered,
                primaryPressed: actionPalette.primary.pressed,
                primaryDisabled: actionPalette.primary.disabled,
                primarySubduedHovered: actionPalette.primary.subduedHovered,
                primarySubduedPressed: actionPalette.primary.subduedPressed,
                // Secondary surfaces
                secondary: actionPalette.secondary.surface,
                secondaryHovered: actionPalette.secondary.hovered,
                secondaryPressed: actionPalette.secondary.pressed,
                secondaryDisabled: actionPalette.secondary.disabled,
                // Critical surfaces
                critical: isDark ? '#FF4D54' : '#E13139',
                criticalHovered: isDark ? '#E63D44' : '#B3272E',
                criticalPressed: '#881D23',
                criticalDisabled: '#F28F94',
                criticalSubdued: isDark ? '#3D1A1C' : '#FDEDEE',
                criticalSubduedHovered: isDark ? '#5A1E21' : '#E131391A',
                criticalSubduedPressed: isDark ? '#7D2629' : '#E131394D',
                // Success surfaces
                success: '#2A865C',
                successSecondary: '#33A370',
                successHovered: '#216B49',
                successPressed: '#195037',
                successDisabled: '#51BF8C',
                successSubdued: isDark ? '#1A3D2E' : '#E3F5ED',
                successSubduedHovered: isDark ? '#245240' : '#2A865C1A',
                successSubduedPressed: isDark ? '#2E6952' : '#2A865C4D',
                // Highlight surfaces
                highlight: '#7D60E6',
                highlight2: isDark ? '#E0E0E0' : RESQ_BRAND_COLORS.navy,
                highlightSubdued: isDark ? '#2D2440' : '#F2EFFC',
                // Warning surfaces
                warning: '#F6D06E',
                warningSecondary: '#EB6A1E',
                warningSubdued: isDark ? '#3D3420' : '#FCF0D0',
                // Decorative surfaces (vendor-specific)
                decorative1: '#7D60E6',
                decorative1Subdued: isDark ? '#2D2440' : '#F2EFFC',
                decorative2: '#EB6A1E',
                decorative2Subdued: isDark ? '#3D2518' : '#FDEEE5',
                decorative3: isDark ? '#1A4D4E' : '#00A1A3',
                decorative3Subdued: isDark ? '#1A3334' : '#DBF6F6',
                decorative4: '#AB8D40',
                decorative4Subdued: isDark ? '#3D3420' : '#FCF0D0',
                // Interactive surfaces
                interactive: isDark ? '#1A4D4E' : '#00A1A3',
                interactiveDisabled: isDark ? '#2D5455' : '#8EE3E3',
                tableSubdued: isDark ? '#1A1F26' : RESQ_BRAND_COLORS.cream,
                // Nora brand surfaces
                nora: '#8FA88B',
                noraDark: '#2F3B34',
                noraGradient:
                    'conic-gradient(from 124deg at 57.19% 134.3%, #6F935E 0deg, #485B40 360deg)',
                noraFooter: '#2A3A49',
                noraDivider: RESQ_BRAND_COLORS.navy,
            },
            text: {
                // Core text
                default: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                subdued: isDark ? '#9AA0A6' : RESQ_BRAND_COLORS.slate,
                disabled: isDark ? '#5F6368' : '#909097',
                onSecondary: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                // Primary text
                primary: actionPalette.primary.text,
                primaryPressed: actionPalette.primary.textPressed,
                primaryDisabled: actionPalette.primary.textDisabled,
                onPrimary: actionPalette.primary.onText,
                // Critical text
                critical: isDark ? '#FF5B62' : '#B3272E',
                criticalPressed: '#881D23',
                criticalDisabled: isDark ? '#7D2629' : '#F28F94',
                onCritical: '#FFFFFF',
                onCriticalSubdued: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                // Success text
                success: isDark ? '#51BF8C' : '#216B49',
                successSecondary: isDark ? '#51BF8C' : '#2A865C',
                successPressed: '#195037',
                successDisabled: isDark ? '#2E6952' : '#51BF8C',
                onSuccess: '#FFFFFF',
                onSuccessSubdued: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                // Highlight text
                highlight: isDark ? '#B1A0F0' : '#6248C5',
                onHighlight: '#FFFFFF',
                // Warning text
                warning: isDark ? '#F6D06E' : '#705C29',
                onWarning: RESQ_BRAND_COLORS.navy,
                onWarningSubdued: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                // Decorative text (vendor-specific)
                decorative1: isDark ? '#B1A0F0' : '#7D60E6',
                decorative2: '#994613',
                decorative3: isDark ? '#4DD4D6' : '#00696B',
                decorative4: '#705C29',
                onDecorative2Subdued: '#994613',
                // Interactive text
                interactive: isDark ? '#4DD4D6' : '#00696B',
                interactiveHovered: '#004299',
                interactivePressed: RESQ_BRAND_COLORS.navy,
                interactiveDisabled: isDark ? '#5F6368' : '#909097',
                placeHolder: isDark ? '#5F6368' : '#909097',
                link: isDark ? '#9DC4FF' : '#005BD1',
            },
            icon: {
                // Core icons
                default: isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy,
                subdued: isDark ? '#9AA0A6' : RESQ_BRAND_COLORS.slate,
                disabled: isDark ? '#5F6368' : '#909097',
                // Primary icons
                primary: actionPalette.primary.icon,
                onPrimary: actionPalette.primary.onText,
                tertiary: actionPalette.primary.icon,
                tertiaryDisabled: actionPalette.primary.iconDisabled,
                // Warning icons
                warning: isDark ? '#F6D06E' : '#8D7434',
                onWarning: RESQ_BRAND_COLORS.navy,
                // Critical icons
                critical: isDark ? '#FF5B62' : '#B3272E',
                criticalDisabled: isDark ? '#7D2629' : '#F28F94',
                onCritical: '#FFFFFF',
                // Success icons
                success: isDark ? '#51BF8C' : '#216B49',
                successDisabled: isDark ? '#2E6952' : '#51BF8C',
                onSuccess: '#FFFFFF',
                // Highlight icons
                highlight: '#7D60E6',
                onHighlight: '#FFFFFF',
                // Decorative icons (vendor-specific)
                decorative1: '#7D60E6',
                decorative1Subdued: isDark ? '#B1A0F0' : '#F4935D',
                decorative2: '#EB6A1E',
                decorative2Subdued: isDark ? '#F4935D' : '#F4935D',
                decorative3: isDark ? '#4DD4D6' : '#00A1A3',
                decorative3Subdued: isDark ? '#4DD4D6' : '#00BEC0',
                decorative4: '#AB8D40',
                decorative4Subdued: '#CAA74B',
                decorative4Bright: '#F6D06E',
                // Interactive icons
                interactive: isDark ? '#9DC4FF' : '#00696B',
                interactiveHovered: isDark ? '#6CA0FF' : '#004299',
                interactivePressed: isDark ? '#2C60CC' : RESQ_BRAND_COLORS.navy,
                interactiveDisabled: isDark ? '#2D6A6B' : '#909097',
                interactiveCritical: '#E13139',
                interactiveCriticalHovered: '#B3272E',
                interactiveCriticalPressed: '#881D23',
                interactiveCriticalDisabled: '#ED646B',
                focused: '#2A76D9',
            },
            border: {
                // Core borders
                default: isDark ? '#5F6368' : '#AAABB0',
                subdued: isDark ? '#3C4043' : '#D4D4D7',
                hovered: isDark ? '#80868B' : '#909097',
                // Primary borders
                primary: actionPalette.primary.border,
                primaryPressed: actionPalette.primary.borderPressed,
                primaryDisabled: actionPalette.primary.borderDisabled,
                onPrimary: isDark ? 'rgba(245, 242, 236, 0.3)' : 'rgba(11, 31, 58, 0.3)',
                // Secondary borders
                secondary: actionPalette.secondary.border,
                secondaryPressed: actionPalette.secondary.borderPressed,
                secondaryDisabled: actionPalette.secondary.borderDisabled,
                onSecondary: isDark ? 'rgba(245, 242, 236, 0.3)' : 'rgba(11, 31, 58, 0.3)',
                // Critical borders
                critical: isDark ? '#FF4D54' : '#E13139',
                criticalPressed: '#881D23',
                criticalDisabled: '#F28F94',
                criticalSubdued: isDark ? '#7D2629' : '#ED646B',
                onCritical: isDark ? 'rgba(245, 242, 236, 0.3)' : 'rgba(11, 31, 58, 0.3)',
                // Success borders
                success: '#2A865C',
                successPressed: '#195037',
                successDisabled: '#51BF8C',
                successSubdued: '#33A370',
                onSuccess: isDark ? 'rgba(245, 242, 236, 0.3)' : 'rgba(11, 31, 58, 0.3)',
                // Highlight borders
                highlight: '#7D60E6',
                highlightSubdued: '#9780EB',
                // Warning borders
                warning: isDark ? '#F6D06E' : '#8D7434',
                warningSubdued: '#AB8D40',
                // Decorative borders (vendor-specific)
                decorative1: isDark ? '#9780EB' : '#7D60E6',
                decorative2: '#EB6A1E',
                decorative3: isDark ? '#4DD4D6' : '#00A1A3',
                decorative4: '#AB8D40',
                // Interactive borders
                interactive: isDark ? '#4DD4D6' : '#00696B',
                focused: '#2A76D9',
                dark: isDark ? '#80868B' : RESQ_BRAND_COLORS.slate,
            },
            divider: {
                default: isDark ? '#3C4043' : '#D4D4D7',
            },
        },
    });

    const existingTabs = vendorTheme.components?.MuiTabs ?? {};
    const existingTabStyles = existingTabs.styleOverrides ?? {};
    const indicatorBase =
        ((existingTabStyles as Record<string, unknown>)?.indicator as Record<string, unknown>) ||
        {};
    const rootBase =
        ((existingTabStyles as Record<string, unknown>)?.root as Record<string, unknown>) || {};

    vendorTheme.components = {
        ...vendorTheme.components,
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 600,
                    padding: vendorTheme.spacing(0, 0, 1),
                    marginRight: vendorTheme.spacing(4),
                    minWidth: 'unset !important',
                    maxWidth: 'unset !important',
                    minHeight: 'unset !important',
                    lineHeight: 'unset !important',
                    color: vendorTheme.resqPalette.text.subdued,
                    '&.Mui-selected': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '&.Mui-focused': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                },
            },
        },
        MuiTabs: {
            ...existingTabs,
            styleOverrides: {
                ...existingTabStyles,
                indicator: {
                    ...indicatorBase,
                    height: '3px',
                    borderRadius: vendorTheme.spacing(1),
                    backgroundColor: vendorTheme.resqPalette.surface.primary,
                },
                root: {
                    ...rootBase,
                    minHeight: 'unset',
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    padding: 0,
                    border: 'none',
                    [vendorTheme.breakpoints.down('lg')]: {
                        paddingLeft: vendorTheme.spacing(4),
                        paddingRight: vendorTheme.spacing(4),
                        borderRadius: 0,
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                    '&.Mui-selected': {
                        backgroundColor: vendorTheme.resqPalette.surface.selected,
                        '&:hover': {
                            backgroundColor: vendorTheme.resqPalette.surface.hovered,
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    '.MuiIconButton-root &': {
                        color: 'inherit',
                    },
                    '.MuiIconButton-root.Mui-disabled &': {
                        color: vendorTheme.resqPalette.icon.disabled,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    '&.Mui-disabled': {
                        color: vendorTheme.resqPalette.icon.disabled,
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.subdued,
                    '& .MuiSvgIcon-root': {
                        color: vendorTheme.resqPalette.icon.subdued,
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    minWidth: '40px',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
                primary: {
                    color: vendorTheme.resqPalette.text.default,
                },
                secondary: {
                    color: vendorTheme.resqPalette.text.subdued,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: vendorTheme.resqPalette.surface.tableSubdued,
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                },
                body: {
                    color: vendorTheme.resqPalette.text.default,
                    borderTop: `1px solid ${vendorTheme.resqPalette.divider.default}`,
                },
                footer: {
                    color: vendorTheme.resqPalette.text.default,
                    borderTop: `1px solid ${vendorTheme.resqPalette.divider.default}`,
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.subdued,
                    '&:hover': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '&.Mui-active': {
                        color: vendorTheme.resqPalette.text.default,
                        '& .MuiTableSortLabel-icon': {
                            color: vendorTheme.resqPalette.icon.default,
                        },
                    },
                },
                icon: {
                    color: vendorTheme.resqPalette.icon.subdued,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
                selectLabel: {
                    color: vendorTheme.resqPalette.text.default,
                },
                displayedRows: {
                    color: vendorTheme.resqPalette.text.default,
                },
                select: {
                    color: vendorTheme.resqPalette.text.default,
                },
                selectIcon: {
                    color: vendorTheme.resqPalette.icon.default,
                },
                actions: {
                    color: vendorTheme.resqPalette.icon.default,
                    '& .MuiIconButton-root': {
                        color: `${vendorTheme.resqPalette.icon.default} !important`,
                        '&.Mui-disabled': {
                            color: `${vendorTheme.resqPalette.icon.disabled} !important`,
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                    boxShadow: 'none',
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&.MuiTypography-body3': {
                        color: vendorTheme.resqPalette.text.subdued,
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                    '&:before': {
                        backgroundColor: vendorTheme.resqPalette.divider.default,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        color: vendorTheme.resqPalette.icon.default,
                    },
                },
                expandIconWrapper: {
                    color: vendorTheme.resqPalette.icon.default,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    '& a': {
                        color: vendorTheme.resqPalette.text.link,
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: vendorTheme.resqPalette.background.default,
                    color: vendorTheme.resqPalette.text.default,
                    // React Big Calendar overrides for dark mode
                    '& .rbc-today': {
                        backgroundColor: vendorTheme.resqPalette.surface.highlightSubdued,
                    },
                    '& .rbc-selected-cell': {
                        backgroundColor: `${vendorTheme.resqPalette.surface.selected} !important`,
                    },
                    '& .rbc-now': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-current': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-off-range-bg': {
                        backgroundColor: vendorTheme.resqPalette.surface.subdued,
                    },
                    '& .rbc-header': {
                        color: vendorTheme.resqPalette.text.default,
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-date-cell': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-month-view': {
                        backgroundColor: vendorTheme.resqPalette.surface.default,
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-day-bg': {
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-month-row': {
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                },
            },
        },
    };

    return vendorTheme;
};

/**
 * @deprecated Use buildVendorTheme('light') instead. This static export will be removed in Phase 2.
 */
export const vendorTheme = buildVendorTheme('light');
